/**
 * Modificar Precio Motor — migración web de ares9756.
 *
 * Busca una factura al por mayor por su número y permite modificar el precio
 * de cada chasis o quitar un chasis de la factura. Ambas operaciones ajustan
 * el total de la factura y recalculan las cuotas: a diferencia del
 * escritorio, solo se redistribuyen las cuotas sin pagos aplicados (las ya
 * cobradas no se tocan) y al quitar un chasis el vehículo vuelve a estado
 * disponible.
 */
import React, { useState, useMemo, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { etiquetaCuota } from "modules/ventasxmayor/impresionxmayor";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Busque una factura por su número
      </Typography>
    </Stack>
  );
}

export default function ModificarPrecioMotor() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [nofact, setNofact] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [factura, setFactura] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [cuotas, setCuotas] = useState([]);

  // Autocomplete de facturas: búsqueda en el servidor por número o cliente.
  const [opciones, setOpciones] = useState([]);
  const [cargandoOpciones, setCargandoOpciones] = useState(false);
  const timerBusqueda = useRef(null);
  const resaltada = useRef(null); // opción bajo el cursor del desplegable

  useEffect(() => () => clearTimeout(timerBusqueda.current), []);

  const buscarOpciones = async (q) => {
    setCargandoOpciones(true);
    try {
      const { ok, data } = await post("/api/xmayor_facturas_buscar", { q, limite: 20 });
      setOpciones(ok && Array.isArray(data.facturas) ? data.facturas : []);
    } finally {
      setCargandoOpciones(false);
    }
  };

  // Un crédito ya cobrado completo no se toca: al cambiar el total, vplicado
  // quedaría fuera de cuadre y el backend solo cierra facturas, nunca las
  // reabre. El contado nace con status 'C' aunque no sea "cobrada después",
  // así que se excluye para poder corregir un precio mal digitado.
  const saldada = factura?.status === "C" && factura?.tipofactura === "Credito";
  const bloqueada = !!factura?.anulada || !!saldada;

  const motivoBloqueo = factura?.anulada
    ? "La factura está anulada — no se pueden modificar sus precios ni sus chasis."
    : "La factura está saldada — no se pueden modificar sus precios ni sus chasis.";

  // Chip de estado de la factura (Anulada / Saldada / Pendiente).
  const estadoFactura = (f) => {
    if (f.anulada) return { label: "Anulada", sx: { bgcolor: "#FFEBEE", color: "#C62828" } };
    if (f.status === "C") return { label: "Saldada", sx: { bgcolor: "#E8F5E9", color: "#2E7D32" } };
    return { label: "Pendiente", sx: { bgcolor: FP.color.infoSoft, color: FP.color.info } };
  };
  const estado = factura ? estadoFactura(factura) : null;

  const buscar = async (id = null) => {
    const numero = id || toNum(nofact);
    if (numero <= 0) {
      showAlert({ message: "Debe indicar el número de la factura", color: "warning" });
      return;
    }
    setBuscando(true);
    try {
      const { ok, data } = await post("/api/xmayor_factura_detalle", { id: numero });
      if (ok) {
        setFactura(data.factura || null);
        // Las líneas devueltas (consignado) son historial: no se editan ni
        // cuentan en el total (el backend también las excluye).
        setLineas(
          (data.lineas || [])
            .filter((l) => l.devuelto !== "S")
            .map((l) => ({
              ...l,
              id: l.chasis,
              precio: String(toNum(l.preciomotor)),
            }))
        );
        setCuotas((data.cuotas || []).map((c) => ({ ...c, id: c.numero_pago })));
      } else {
        setFactura(null);
        setLineas([]);
        setCuotas([]);
        showAlert({ message: data.Error || "No se pudo cargar la factura", color: "error" });
      }
    } finally {
      setBuscando(false);
    }
  };

  const setPrecio = (chasis, valor) => {
    setLineas((prev) => prev.map((l) => (l.chasis === chasis ? { ...l, precio: valor } : l)));
  };

  const totalVivo = useMemo(() => lineas.reduce((acc, l) => acc + toNum(l.precio), 0), [lineas]);

  // El precio no puede quedar por debajo del mínimo del chasis (producto.mprecio,
  // que el backend devuelve ya convertido a la moneda de la factura).
  const bajoElMinimo = (l) => toNum(l.preciominimo) > 0 && toNum(l.precio) < toNum(l.preciominimo);
  const hayBajoMinimo = useMemo(() => lineas.some(bajoElMinimo), [lineas]);

  const avisarResultado = (data, prefijo) => {
    let msg = `${prefijo}. Nuevo total RD$ ${fmtMoney(data.total)}`;
    if (toNum(data.cuotasRecalculadas) > 0) {
      msg += `. Se recalcularon ${data.cuotasRecalculadas} cuotas`;
    }
    if (data.cerrada) msg += ". La factura quedó saldada";
    showAlert({ message: msg, color: "success" });
  };

  // ---- Guardar precios ----
  const [guardando, setGuardando] = useState(false);

  const guardarPrecios = async () => {
    if (lineas.some((l) => toNum(l.precio) < 0)) {
      showAlert({ message: "El precio de un motor no puede ser negativo", color: "warning" });
      return;
    }
    const bajoMinimo = lineas.find(bajoElMinimo);
    if (bajoMinimo) {
      showAlert({
        message: `El precio del chasis ${
          bajoMinimo.chasis
        } no puede ser menor al precio mínimo (RD$ ${fmtMoney(bajoMinimo.preciominimo)})`,
        color: "warning",
      });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/xmayor_precios_guardar", {
        id: factura.id,
        items: lineas.map((l) => ({ chasis: l.chasis, precio: toNum(l.precio) })),
      });
      if (ok) {
        avisarResultado(data, data.ok || "Precios guardados");
        buscar(factura.id);
      } else {
        showAlert({ message: data.Error || "No se pudieron guardar los precios", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  // ---- Quitar chasis ----
  const [target, setTarget] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const eliminarChasis = async () => {
    setEliminando(true);
    try {
      const { ok, data } = await post("/api/xmayor_chasis_eliminar", {
        id: factura.id,
        chasis: target.chasis,
      });
      if (ok) {
        setTarget(null);
        avisarResultado(data, data.ok || "Chasis eliminado de la factura");
        buscar(factura.id);
      } else {
        showAlert({ message: data.Error || "No se pudo quitar el chasis", color: "error" });
      }
    } finally {
      setEliminando(false);
    }
  };

  const columnasLineas = [
    { field: "chasis", headerName: "Chasis", width: 190 },
    { field: "marca", headerName: "Marca", width: 120 },
    { field: "modelo", headerName: "Modelo", width: 120 },
    { field: "colore", headerName: "Color", width: 110 },
    {
      field: "chasispagado",
      headerName: "",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.chasispagado === "S" ? (
          <Chip
            size="small"
            label="Saldado"
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ) : null,
    },
    {
      field: "preciominimo",
      headerName: "Mínimo",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => (toNum(value) > 0 ? fmtMoney(value) : "—"),
    },
    {
      field: "precio",
      headerName: "Precio Motor",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.precio}
          onChange={(e) => setPrecio(params.row.chasis, e.target.value)}
          disabled={bloqueada}
          error={bajoElMinimo(params.row)}
          title={
            bajoElMinimo(params.row)
              ? `Menor al precio mínimo (RD$ ${fmtMoney(params.row.preciominimo)})`
              : ""
          }
          inputProps={{ style: { textAlign: "right" } }}
          sx={fpInputSx}
        />
      ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => setTarget(params.row)}
          disabled={bloqueada || lineas.length <= 1}
          sx={btnOutline(FP.color.danger)}
        >
          Quitar chasis
        </Button>
      ),
    },
  ];

  const columnasCuotas = [
    {
      field: "numero_pago",
      headerName: "Cuota#",
      width: 90,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) => etiquetaCuota(value),
    },
    { field: "fecha", headerName: "Fecha", width: 120 },
    {
      field: "valor",
      headerName: "Valor",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "valor_pagado",
      headerName: "Pagado",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    { field: "status", headerName: "Status", width: 90, align: "center", headerAlign: "center" },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Modificar Precio Motor"
        subtitle="Ajusta los precios de los chasis de una factura al por mayor y recalcula sus cuotas"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Búsqueda */}
        <Box sx={sectionBox}>
          <SectionTitle icon={SearchIcon} color={FP.color.accentBlue}>
            Factura
          </SectionTitle>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5} md={4}>
              <Autocomplete
                fullWidth
                freeSolo
                disablePortal
                options={opciones}
                loading={cargandoOpciones}
                filterOptions={(x) => x}
                inputValue={nofact}
                onOpen={() => buscarOpciones("")}
                onInputChange={(_, valor, motivo) => {
                  if (motivo === "reset") return;
                  setNofact(valor);
                  if (motivo !== "input") return;
                  clearTimeout(timerBusqueda.current);
                  timerBusqueda.current = setTimeout(() => buscarOpciones(valor.trim()), 280);
                }}
                onChange={(_, opt) => {
                  if (opt && typeof opt === "object") {
                    setNofact(String(opt.id));
                    buscar(opt.id);
                  }
                }}
                getOptionLabel={(o) =>
                  typeof o === "object" && o ? `${o.id} — ${o.cliente || "S/N"}` : String(o || "")
                }
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                onHighlightChange={(_, opt) => {
                  resaltada.current = opt;
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        #{o.id} — {o.cliente || "S/N"}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {o.fecha} · {o.cantidad} chasis · RD$ {fmtMoney(o.valor)}
                        {o.anulada ? " · Anulada" : ""}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Factura (número o cliente)"
                    // Enter con un número escrito busca directo, como antes;
                    // con una opción resaltada manda el onChange del listado.
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !resaltada.current && /^\d+$/.test(nofact.trim())) {
                        buscar();
                      }
                    }}
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                startIcon={
                  buscando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={() => buscar()}
                disabled={buscando}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Buscar
              </Button>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
              {factura && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={factura.cliente}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Fecha ${factura.fecha}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Valor RD$ ${fmtMoney(factura.valor)}`}
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                  <Chip
                    label={`Pagado RD$ ${fmtMoney(factura.vplicado)}`}
                    sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
                  />
                  <Chip label={estado.label} sx={{ ...estado.sx, fontWeight: 700 }} />
                </Stack>
              )}
            </Grid>
          </Grid>
          {bloqueada && (
            <Box
              sx={{
                mt: 2,
                p: 1.6,
                borderRadius: 1.8,
                bgcolor: FP.color.warningSoft,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <WarningAmberOutlinedIcon sx={{ color: FP.color.warning, fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#92400E" }}>
                {motivoBloqueo}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Líneas / precios */}
        <Box sx={sectionBox}>
          <SectionTitle icon={DirectionsCarOutlinedIcon} color={FP.color.success}>
            Chasis de la factura
          </SectionTitle>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={lineas}
              columns={columnasLineas}
              loading={buscando}
              rowHeight={52}
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
              disableRowSelectionOnClick
            />
          </Box>
          {factura && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
              sx={{ mt: 2 }}
            >
              <Stack spacing={0.4}>
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                  Total: RD$ {fmtMoney(totalVivo)}
                </Typography>
                {hayBajoMinimo && (
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: FP.color.danger }}>
                    Hay precios por debajo del mínimo del chasis
                  </Typography>
                )}
              </Stack>
              <Button
                startIcon={
                  guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                }
                onClick={guardarPrecios}
                disabled={guardando || bloqueada || hayBajoMinimo || lineas.length === 0}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Guardar precios
              </Button>
            </Stack>
          )}
        </Box>

        {/* Cuotas */}
        {factura && (
          <Box sx={{ ...sectionBox, mb: 0 }}>
            <SectionTitle icon={ReceiptLongOutlinedIcon} color={FP.color.info}>
              Cuotas de la factura
            </SectionTitle>
            <Box sx={{ bgcolor: "#fff" }}>
              <DataGrid
                autoHeight
                rows={cuotas}
                columns={columnasCuotas}
                rowHeight={46}
                pageSizeOptions={[10, 25]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                slots={{ noRowsOverlay: NoRowsOverlay }}
                sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "140px" }}
                disableRowSelectionOnClick
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Confirmación de quitar chasis */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>Quitar chasis</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Chasis <b>{target?.chasis}</b> (
            {[target?.marca, target?.modelo].filter(Boolean).join(" ")}) de la factura{" "}
            <b>{factura?.id}</b>. Se quitará de la factura, se recalcularán las cuotas sin pagos
            aplicados con el nuevo total y el vehículo volverá a estado disponible. ¿Desea
            continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              eliminando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={eliminarChasis}
            disabled={eliminando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, quitar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
